import type { BrainCard, CardTask } from 'elowen-plugin-ui-kit';

/** The claimed card's checklist rows, read off the cards the stream has already delivered for the session
 *  on screen (an owner conversation or a drilled-in sub-agent) — no request of its own and nothing to poll.
 *
 *  The caller names the card id, so this projection never decides ownership by plugin name or by a card id
 *  baked into core. Card items are a generic mechanism, so any plugin may emit rows with ids of its own;
 *  those ids are that plugin's handles and mean nothing to the todo API, which is what a tick box built from
 *  them would PATCH. Everything else keeps reporting itself in the transcript. */
export function cardTasks(cards: readonly BrainCard[], cardId: string): CardTask[] {
  const card = cards.find((entry) => entry.id === cardId);
  return (card?.items ?? []).map((item) => ({
    ...(item.id !== undefined ? { id: item.id } : {}),
    label: item.label ?? item.text,
    status: item.status ?? 'pending',
    ...(item.startedAt !== undefined ? { startedAt: item.startedAt } : {}),
    ...(item.durationMs !== undefined ? { durationMs: item.durationMs } : {}),
    ...(item.owner !== undefined ? { owner: item.owner } : {}),
    blockedBy: item.blockedBy ?? [],
  }));
}

/** Whether the card's own rows can be ticked, i.e. whether every one of them carries a task id. A card
 *  older than structured items has rows but no handles, and a half-addressable list would offer controls
 *  that work on some rows only — so it is all or nothing. */
export const cardTasksAddressable = (rows: readonly CardTask[]): rows is readonly (CardTask & { id: string })[] =>
  rows.length > 0 && rows.every((row) => !!row.id);

/** Whether one card can be ticked at all: the same all-or-nothing rule, taken over the rows this module
 *  would project for it, so a card without items, an empty card and a card with one handleless row all
 *  answer false. The row list and the card keep one predicate rather than two drifting copies. */
export const cardItemsAddressable = (card: BrainCard): boolean =>
  cardTasksAddressable(cardTasks([card], card.id));
